import gql from "graphql-tag";
import * as React from "react";
import {useHistory} from "react-router-dom";

import Button from "@khanacademy/wonder-blocks-button";
import {useMutation} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {doNotTranslateYet} from "@repo/i18n";
import {PermissionsInfo} from "@repo/shared-core";
import {getLoginUrl} from "@repo/urls";

import type {ComputingCodeMultiFile} from "#content-library/types/types.ts";

import type {
    spinOffPythonProgram as GqlResponse,
    spinOffPythonProgramVariables as GqlVariables,
} from "./__graphql-types__/spinOffPythonProgram.ts";
import CreatePythonUserProgramModal from "./create-python-user-program-modal.tsx";
import {pyIconUrl} from "./thumbnail-icon.ts";

export const spinOffProgramMutation: GqlOperation<GqlResponse, GqlVariables> =
    gqlOp(gql`
        mutation spinOffPythonProgram(
            $originProgramOrContentId: String!
            $originProgramKind: SpinoffOriginKind!
            $originRevisionId: ID
            $title: String!
            $revision: ProgramRevisionInput!
            $curationNodeSlug: String!
        ) {
            spinOffProgram(
                origin: {
                    programOrContentId: $originProgramOrContentId
                    kind: $originProgramKind
                }
                originRevisionId: $originRevisionId
                title: $title
                revision: $revision
                curationNodeSlug: $curationNodeSlug
            ) {
                program {
                    id
                    url
                }
                error {
                    code
                    debugMessage
                }
            }
        }
    `);

type Props = {
    originProgramOrContentId: string;
    originProgramKind: "CHALLENGE" | "PROJECT" | "INTERACTIVE" | "PROGRAM";
    originRevisionId?: string;
    curationNodeSlug: string;
    code: ComputingCodeMultiFile;
    icon?: React.ComponentProps<typeof Button>["startIcon"];
    // onSpinoffSaveClicked is called when the user clicks the save button in the
    // Spin-off modal. It is called before the mutation is sent to the server,
    // so there's no guarantee that the spin-off creation will succeed.
    onSpinoffSaveClicked: () => void;
};

export default function PythonSpinOffButton({
    code,
    originProgramOrContentId,
    originProgramKind,
    originRevisionId,
    curationNodeSlug,
    icon,
    onSpinoffSaveClicked,
}: Props): React.ReactElement {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);

    const history = useHistory();

    const [createSpinOff] = useMutation(spinOffProgramMutation);
    const onSave = async (programName): Promise<boolean> => {
        onSpinoffSaveClicked();

        setIsSaving(true);
        const result = await createSpinOff({
            originProgramOrContentId,
            originProgramKind,
            originRevisionId,
            curationNodeSlug,
            revision: {
                code: JSON.stringify(code),
                configVersion: 0,
                folds: [],
                imageUrl: pyIconUrl,
            },
            title: programName,
        });
        setIsSaving(false);

        if (
            result.status === "success" &&
            result.data.spinOffProgram?.program?.url != null &&
            result.data.spinOffProgram.error == null
        ) {
            history.push(result.data.spinOffProgram.program.url);
            return true;
        }

        return false;
    };

    const onClick = () => {
        if (PermissionsInfo.isLoggedIn) {
            setIsModalOpen(true);
        } else {
            history.push(getLoginUrl(history.location.pathname));
        }
    };

    return (
        <>
            <Button startIcon={icon} kind="secondary" onClick={onClick}>
                {doNotTranslateYet`Spin-off`}
            </Button>
            {isModalOpen && (
                <CreatePythonUserProgramModal
                    onClose={isSaving ? () => {} : () => setIsModalOpen(false)}
                    onSave={onSave}
                    isSaving={isSaving}
                />
            )}
        </>
    );
}
